import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClients } from "@/hooks/useClients";
import { useOrganization } from "@/hooks/useOrganization";
import { useCreateCustomerInvitation } from "@/hooks/useCustomers";

interface InviteCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  preselectedClientId?: string;
}

export function InviteCustomerDialog({
  open,
  onClose,
  preselectedClientId,
}: InviteCustomerDialogProps) {
  const { data: clients = [] } = useClients();
  const { data: org } = useOrganization();
  const invite = useCreateCustomerInvitation();
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState(preselectedClientId ?? "");

  function resetAndClose() {
    setEmail("");
    setClientId(preselectedClientId ?? "");
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !clientId) return;

    await invite.mutateAsync({ email, clientId });
    resetAndClose();
  }

  const activeClients = (clients as any[]).filter(
    (c: any) => c.is_active !== false && (!org?.name || c.name !== org.name)
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && resetAndClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Customer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-client">Client</Label>
            <Select
              value={clientId}
              onValueChange={setClientId}
              disabled={!!preselectedClientId}
            >
              <SelectTrigger id="invite-client">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {activeClients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email Address</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            This will send an invitation email. When accepted, the user will receive the{" "}
            <strong>client_viewer</strong> role and be automatically linked to the selected client.
            If they manage more than one brand, add the others from the Customers list once
            they've accepted.
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={invite.isPending || !email || !clientId}>
              {invite.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
